import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Check, ExternalLink, Loader2, Mail } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Breakthrough Commerce Stories — showcases commercestories.com (Substack)
 * articles. Cards link out to the canonical article; the page's job is
 * discovery + list building.
 */
export default function StoriesPage() {
  const [stories, setStories] = useState(null);
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeError, setSubscribeError] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetch(`${BACKEND_URL}/api/public/stories`)
      .then(r => (r.ok ? r.json() : { stories: [] }))
      .then(data => { if (!cancelled) setStories(data.stories || []); })
      .catch(() => { if (!cancelled) setStories([]); });
    return () => { cancelled = true; };
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.includes('@')) {
      setSubscribeError('Enter a valid email');
      return;
    }
    setSubscribing(true);
    setSubscribeError('');
    try {
      const r = await fetch(`${BACKEND_URL}/api/public/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'stories' }),
      });
      if (r.ok) setSubscribed(true);
      else setSubscribeError('Could not subscribe — try again.');
    } catch {
      setSubscribeError('Network error — try again.');
    }
    setSubscribing(false);
  };

  const formatDate = (raw) => {
    try {
      return new Date(raw).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
      });
    } catch { return ''; }
  };

  return (
    <div className="min-h-screen bg-[#0B1120] text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
      {/* Nav */}
      <nav className="border-b border-[#22304A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#3EF1C7] flex items-center justify-center">
              <span className="text-[#0B1120] font-bold text-sm">CI</span>
            </div>
            <span className="font-semibold">Commerce Index</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/integrations" className="text-sm text-[#94A3B8] hover:text-white">Integrations</Link>
            <Link to="/#pricing" className="text-sm text-[#94A3B8] hover:text-white">Pricing</Link>
            <Link to="/" className="px-4 py-2 bg-[#3EF1C7] text-[#0B1120] rounded-lg font-semibold text-sm hover:bg-[#3EF1C7]/90">
              Find my money — free
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#3EF1C7]/10 border border-[#3EF1C7]/20 mb-6">
          <BookOpen className="w-4 h-4 text-[#3EF1C7]" />
          <span className="text-sm text-[#3EF1C7] font-medium">Commerce Stories</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold mb-4">
          Breakthrough <span className="text-[#3EF1C7]">Commerce Stories</span>
        </h1>
        <p className="text-[#94A3B8] max-w-2xl mx-auto">
          Real merchants, real numbers, real turnarounds — stories from the front
          lines of commerce, published on{' '}
          <a href="https://www.commercestories.com" target="_blank" rel="noopener noreferrer"
             className="text-[#3EF1C7] hover:underline">commercestories.com</a>.
        </p>
      </header>

      {/* Stories grid */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {stories === null && (
          <div className="flex items-center justify-center py-24 text-[#64748B]">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading stories…
          </div>
        )}

        {stories && stories.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {stories.map(story => (
              <a
                key={story.link}
                href={story.link}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col p-6 rounded-xl border border-[#22304A] bg-[#0D1628] hover:border-[#3EF1C7]/40 transition-colors"
              >
                {story.image && (
                  <img src={story.image} alt="" loading="lazy"
                       className="w-full h-40 object-cover rounded-lg mb-4 border border-[#22304A]" />
                )}
                <div className="text-xs text-[#64748B] mb-2">{formatDate(story.published)}</div>
                <h2 className="font-semibold mb-2 group-hover:text-[#3EF1C7] transition-colors">
                  {story.title}
                </h2>
                <p className="text-sm text-[#94A3B8] flex-1">{story.snippet}</p>
                <div className="mt-4 inline-flex items-center gap-1 text-xs text-[#3EF1C7]">
                  Read on Commerce Stories <ExternalLink className="w-3 h-3" />
                </div>
              </a>
            ))}
          </div>
        )}

        {stories && stories.length === 0 && (
          <div className="text-center py-16 mb-16 rounded-xl border border-dashed border-[#22304A]">
            <BookOpen className="w-10 h-10 text-[#64748B] mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">First stories drop soon</h2>
            <p className="text-sm text-[#94A3B8] mb-4">
              Subscribe below and we'll send each breakthrough story as it publishes.
            </p>
            <a href="https://www.commercestories.com" target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-1 text-sm text-[#3EF1C7] hover:underline">
              Visit commercestories.com <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        )}

        {/* Subscribe */}
        <section className="max-w-xl mx-auto text-center p-8 rounded-2xl border border-[#22304A] bg-[#0D1628]">
          <Mail className="w-8 h-8 text-[#3EF1C7] mx-auto mb-3" />
          <h2 className="text-xl font-bold mb-2">Never miss a story</h2>
          <p className="text-sm text-[#94A3B8] mb-6">
            Breakthrough commerce stories, product releases, and what our agents
            are learning — straight to your inbox. No spam, unsubscribe anytime.
          </p>
          {subscribed ? (
            <div className="inline-flex items-center gap-2 text-[#3EF1C7] font-medium">
              <Check className="w-5 h-5" /> You're on the list.
            </div>
          ) : (
            <form onSubmit={subscribe} className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@yourstore.com"
                className="flex-1 px-4 py-3 rounded-lg bg-[#0B1120] border border-[#22304A] text-sm focus:border-[#3EF1C7] focus:outline-none"
              />
              <button
                type="submit"
                disabled={subscribing}
                className="px-5 py-3 bg-[#3EF1C7] text-[#0B1120] rounded-lg font-semibold text-sm hover:bg-[#3EF1C7]/90 disabled:opacity-50"
              >
                {subscribing ? '…' : 'Subscribe'}
              </button>
            </form>
          )}
          {subscribeError && <div className="text-sm text-[#FF6B6B] mt-3">{subscribeError}</div>}
        </section>
      </main>
    </div>
  );
}
